import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import ArticleCard from "@/components/feature/ArticleCard";
import { applySeo } from "@/utils/seo";
import { categories } from "@/mocks/categories";
import { byCategory, hotArticles } from "@/mocks/articles";
import type { Article } from "@/mocks/types";
import NotFound from "@/pages/NotFound";

type SortKey = "latest" | "views";

export default function Channel() {
  const { slug = "" } = useParams();
  const [sortKey, setSortKey] = useState<SortKey>("latest");
  const category = categories.find((c) => c.slug === slug);

  useEffect(() => {
    setSortKey("latest");
    if (!category) return;
    applySeo({
      title: `${category.name} — ${category.short}栏目 | 灼见社`,
      description: `灼见社${category.name}栏目：${category.desc}`,
      keywords: `最新爆料,独家内幕,深度揭秘,${category.short},${category.name}`,
      path: `/channel/${slug}`,
      type: "collection",
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://yusr.com.cn/" },
            { "@type": "ListItem", position: 2, name: category.name },
          ],
        },
        {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: `${category.name} - 灼见社`,
          about: category.name,
        },
      ],
    });
  }, [slug, category]);

  const list = useMemo<Article[]>(() => {
    const base = byCategory(slug);
    const sorted = [...base];
    if (sortKey === "latest") {
      sorted.sort((a, b) => (a.publishDate > b.publishDate ? -1 : 1));
    } else {
      sorted.sort((a, b) => parseInt(b.views) - parseInt(a.views));
    }
    return sorted;
  }, [slug, sortKey]);

  if (!category) return <NotFound />;

  const tags = Array.from(
    new Set(list.flatMap((a) => a.tags)).values()
  ).slice(0, 10);

  const featuredOfCat = list.find((a) => a.featured) ?? list[0];

  return (
    <>
      <SiteHeader />
      <main>
        <div className="border-b border-background-200/70 bg-background-100">
          <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-12">
            <nav
              aria-label="面包屑"
              className="mb-4 flex items-center gap-1.5 text-sm text-foreground-500"
            >
              <Link to="/" className="hover:text-primary-600">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700">{category.name}</span>
            </nav>
            <h1 className="font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
              {category.name}
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-foreground-600 md:text-base">
              {category.desc}
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {categories.map((c) => (
                <Link
                  key={c.slug}
                  to={`/channel/${c.slug}`}
                  className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                    c.slug === category.slug
                      ? "bg-primary-500 font-medium text-background-50"
                      : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                  }`}
                >
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-7xl px-4 pt-8 md:px-6">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <div className="mb-5 flex items-center justify-between">
                <p className="text-sm text-foreground-500">
                  共 <span className="font-medium text-foreground-900">{list.length}</span>{" "}
                  篇深度内容
                </p>
                <div className="flex items-center gap-1 rounded-full border border-background-200 bg-background-100 p-1">
                  <button
                    type="button"
                    onClick={() => setSortKey("latest")}
                    className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                      sortKey === "latest"
                        ? "bg-background-50 font-medium text-foreground-950 shadow-sm"
                        : "text-foreground-600"
                    }`}
                  >
                    最新发布
                  </button>
                  <button
                    type="button"
                    onClick={() => setSortKey("views")}
                    className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                      sortKey === "views"
                        ? "bg-background-50 font-medium text-foreground-950 shadow-sm"
                        : "text-foreground-600"
                    }`}
                  >
                    阅读最多
                  </button>
                </div>
              </div>

              {featuredOfCat && (
                <div className="mb-6 rounded-lg border border-background-200/70 bg-background-100 p-4">
                  <ArticleCard article={featuredOfCat} layout="horizontal" showDeck />
                </div>
              )}

              {list.length === 0 ? (
                <div className="rounded-lg border border-background-200 bg-background-100 p-10 text-center">
                  <i className="ri-inbox-line text-3xl text-foreground-400"></i>
                  <p className="mt-3 text-sm text-foreground-600">
                    本栏目内容筹备中，敬请期待。
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {list.map((a) => (
                    <div
                      key={a.id}
                      className="rounded-lg border border-background-200/70 bg-background-50 p-4"
                    >
                      <ArticleCard article={a} layout="horizontal" showDeck />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <aside className="space-y-6">
              <div className="rounded-lg border border-background-200/70 bg-background-100 px-3 py-4">
                <h2 className="mb-2 flex items-center gap-2 px-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-fire-fill text-accent-500"></i> 全站热榜
                </h2>
                <ol className="space-y-1">
                  {hotArticles.map((a, i) => (
                    <li key={a.id}>
                      <Link
                        to={`/article/${a.id}`}
                        className="group flex gap-3 rounded-md px-2 py-2.5 hover:bg-background-50"
                      >
                        <span
                          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md font-heading text-sm font-bold ${
                            i < 3 ? "bg-accent-100 text-accent-800" : "bg-secondary-100 text-foreground-600"
                          }`}
                        >
                          {i + 1}
                        </span>
                        <span className="line-clamp-2 font-heading text-sm font-bold leading-snug text-foreground-900 group-hover:text-primary-600">
                          {a.title}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ol>
              </div>

              {tags.length > 0 && (
                <div className="rounded-lg border border-background-200/70 bg-background-100 p-5">
                  <h2 className="font-heading text-base font-bold text-foreground-950">
                    本栏标签
                  </h2>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {tags.map((t) => (
                      <span
                        key={t}
                        className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900"
                      >
                        #{t}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="rounded-lg border border-background-200/70 bg-accent-100 p-5">
                <h2 className="font-heading text-base font-bold text-accent-900">
                  有线索要爆料？
                </h2>
                <p className="mt-2 text-sm leading-6 text-accent-900/80">
                  掌握一手内幕却不知如何开口？我们保护每一位信源。
                </p>
                <a
                  href="mailto:contact@zhuojianshe.example"
                  className="mt-3 inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-accent-800 hover:text-accent-950"
                >
                  提供线索
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <SiteFooter />
    </>
  );
}